import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef, useCallback } from "react";
import { getSyncJobs, triggerSync, cancelJob, retryJob, processQueue, type SyncJob, type JobLog } from "@/lib/api";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  BookOpen,
  MessageSquare,
  Layers,
  FileText,
  RotateCcw,
  StopCircle,
  Terminal,
  Circle,
  ChevronDown,
  History,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://atlas-ops-agent.atlas-kitchen.workers.dev";

interface SubJobCounts {
  queued: number;
  processing: number;
  completed: number;
  failed: number;
  timedOut: number;
  list?: string[]; // Individual sub-job statuses for rendering balls
}

const syncSources = [
  {
    id: "help",
    name: "Help center",
    description: "Sync articles from help.atlas.kitchen",
    icon: BookOpen,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
  },
  {
    id: "slack",
    name: "Slack history",
    description: "Sync conversations from #customer-ops",
    icon: MessageSquare,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
  },
  {
    id: "linear",
    name: "Linear issues",
    description: "Sync issues and comments from Linear",
    icon: Layers,
    color: "text-indigo-500",
    bgColor: "bg-indigo-500/10",
  },
  {
    id: "notion",
    name: "Notion docs",
    description: "Sync documentation from Notion",
    icon: FileText,
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
  },
];

// Sub-job status summary (counts with icons) - for source cards
function SubJobSummary({ subJobs }: { subJobs?: SubJobCounts }) {
  if (!subJobs) return null;

  const total = subJobs.queued + subJobs.processing + subJobs.completed + subJobs.failed;
  if (total === 0) return null;

  return (
    <div className="flex items-center gap-1 mt-1">
      {subJobs.queued > 0 && (
        <div className="flex items-center gap-0.5" title={`${subJobs.queued} queued`}>
          <Circle className="h-2 w-2 fill-blue-500 text-blue-500" />
          <span className="text-[10px] text-blue-500">{subJobs.queued}</span>
        </div>
      )}
      {subJobs.processing > 0 && (
        <div className="flex items-center gap-0.5" title={`${subJobs.processing} processing`}>
          <Circle className="h-2 w-2 fill-yellow-500 text-yellow-500 animate-pulse" />
          <span className="text-[10px] text-yellow-500">{subJobs.processing}</span>
        </div>
      )}
      {subJobs.completed > 0 && (
        <div className="flex items-center gap-0.5" title={`${subJobs.completed} completed`}>
          <Circle className="h-2 w-2 fill-green-500 text-green-500" />
          <span className="text-[10px] text-green-500">{subJobs.completed}</span>
        </div>
      )}
      {subJobs.failed > 0 && (
        <div className="flex items-center gap-0.5" title={`${subJobs.failed} failed`}>
          <Circle className="h-2 w-2 fill-red-500 text-red-500" />
          <span className="text-[10px] text-red-500">{subJobs.failed}</span>
        </div>
      )}
    </div>
  );
}

// Individual sub-job balls - for log header
function SubJobBalls({ subJobs }: { subJobs?: SubJobCounts }) {
  if (!subJobs?.list || subJobs.list.length === 0) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queued': return 'bg-blue-500';
      case 'processing': return 'bg-yellow-500 animate-pulse';
      case 'completed': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-[3px]" title={`${subJobs.list.length} sub-jobs`}>
      {subJobs.list.map((status, i) => (
        <div
          key={i}
          className={`h-[6px] w-[6px] rounded-full ${getStatusColor(status)}`}
          title={status}
        />
      ))}
    </div>
  );
}

export function SyncPage() {
  const queryClient = useQueryClient();
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [jobLogs, setJobLogs] = useState<JobLog[]>([]);
  const [subJobCounts, setSubJobCounts] = useState<Record<string, SubJobCounts>>({});
  const eventSourceRef = useRef<EventSource | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const { data: jobs, isLoading } = useQuery({
    queryKey: ["sync-jobs"],
    queryFn: getSyncJobs,
    refetchInterval: 5000,
  });

  const triggerMutation = useMutation({
    mutationFn: ({ source, mode }: { source: string; mode?: 'recent' | 'full' }) => triggerSync(source, mode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sync-jobs"] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sync-jobs"] });
    },
  });

  const retryMutation = useMutation({
    mutationFn: (jobId: string) => retryJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sync-jobs"] });
    },
  });

  const processQueueMutation = useMutation({
    mutationFn: () => processQueue(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sync-jobs"] });
    },
  });

  // Scroll logs to bottom when new logs arrive
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [jobLogs]);

  // Subscribe to SSE for job updates
  const subscribeToJob = useCallback((jobId: string) => {
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setJobLogs([]);

    // EventSource doesn't support custom headers, so pass token as query param
    const token = localStorage.getItem('admin_token');
    const streamUrl = `${API_BASE}/api/admin/sync/jobs/${jobId}/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const eventSource = new EventSource(streamUrl);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'status') {
          if (data.subJobs) {
            setSubJobCounts(prev => ({ ...prev, [jobId]: data.subJobs }));
          }
          // Refresh job list when status changes
          queryClient.invalidateQueries({ queryKey: ["sync-jobs"] });
        } else if (data.type === 'log') {
          setJobLogs(prev => [...prev, data as JobLog]);
        } else if (data.type === 'done') {
          eventSource.close();
          queryClient.invalidateQueries({ queryKey: ["sync-jobs"] });
        }
      } catch (err) {
        console.error('SSE parse error:', err);
      }
    };

    eventSource.onerror = () => {
      // Just close on error - the user can manually reconnect by selecting the job again
      eventSource.close();
    };
  }, [queryClient]);

  // Fetch sub-jobs and logs for a job (for non-running jobs)
  const fetchSubJobs = useCallback(async (jobId: string) => {
    // Close any existing EventSource to prevent logs from other jobs being appended
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    // Clear existing logs before fetching new ones to prevent mixing
    setJobLogs([]);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/api/admin/sync/jobs/${jobId}/sub-jobs`, {
        headers: token ? { 'X-Admin-Token': token } : {}
      });
      if (response.ok) {
        const data = await response.json();
        if (data.subJobs) {
          setSubJobCounts(prev => ({ ...prev, [jobId]: data.subJobs }));
        }
        if (data.logs) {
          setJobLogs(data.logs);
        }
      }
    } catch (err) {
      console.error('Failed to fetch sub-jobs:', err);
    }
  }, []);

  // Handle job selection
  const handleSelectJob = useCallback((jobId: string) => {
    setSelectedJob(jobId);
    const job = jobs?.find(j => j.id === jobId);
    if (job && (job.status === 'running' || job.status === 'pending')) {
      subscribeToJob(jobId);
    } else {
      // For non-running jobs, fetch sub-jobs directly
      fetchSubJobs(jobId);
    }
  }, [jobs, subscribeToJob, fetchSubJobs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
    };
  }, []);

  // Compute initial selection - prefer running job, fallback to first job
  // This runs on every render but only affects state when there's no selection
  const computedSelectedJob = selectedJob ?? (() => {
    if (!jobs?.length) return null;
    const runningJob = jobs.find(j => j.status === 'running' || j.status === 'pending');
    return runningJob?.id ?? jobs[0]?.id ?? null;
  })();

  // Only subscribe/fetch when computedSelectedJob changes and we don't have selectedJob yet
  // This initializes the SSE subscription which is an external system sync - valid use of effect
  useEffect(() => {
    if (computedSelectedJob && !selectedJob) {
      const job = jobs?.find(j => j.id === computedSelectedJob);
      if (job && (job.status === 'running' || job.status === 'pending')) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing SSE subscription
        subscribeToJob(computedSelectedJob);
      } else if (job) {
         
        fetchSubJobs(computedSelectedJob);
      }
      setSelectedJob(computedSelectedJob);
    }
  }, [computedSelectedJob, selectedJob, jobs, subscribeToJob, fetchSubJobs]);

  const getStatusBadge = (status: SyncJob["status"]) => {
    switch (status) {
      case "running":
        return (
          <Badge className="bg-blue-100 text-blue-800 text-[10px] h-5">
            <Loader2 className="h-2.5 w-2.5 animate-spin mr-0.5" />
            Running
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="success" className="text-[10px] h-5">
            <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
            Done
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive" className="text-[10px] h-5">
            <XCircle className="h-2.5 w-2.5 mr-0.5" />
            Failed
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="secondary" className="text-[10px] h-5">
            <Clock className="h-2.5 w-2.5 mr-0.5" />
            Pending
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="outline" className="text-orange-600 border-orange-600 text-[10px] h-5">
            <StopCircle className="h-2.5 w-2.5 mr-0.5" />
            Cancelled
          </Badge>
        );
      default:
        return <Badge variant="outline" className="text-[10px] h-5">{status}</Badge>;
    }
  };

  const getLatestJobForSource = (source: string) => {
    return jobs?.find((j) => j.source === source);
  };

  const isSourceRunning = (source: string) => {
    const job = getLatestJobForSource(source);
    return job?.status === "running" || job?.status === "pending";
  };

  const selectedJobData = jobs?.find(j => j.id === selectedJob);

  return (
    <Layout>
      <div className="h-[calc(100vh-120px)]">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Sync Management</h1>
            <p className="text-muted-foreground text-sm">
              Manage your RAG pipeline data sources
            </p>
          </div>
          <Button
            onClick={() => processQueueMutation.mutate()}
            disabled={processQueueMutation.isPending}
            size="sm"
            variant="outline"
          >
            {processQueueMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-3 w-3 mr-1" />
            )}
            Process Queue
          </Button>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-[320px_1fr] gap-4 h-[calc(100%-60px)]">
          {/* Left Column - Sync Sources & Jobs */}
          <div className="space-y-3 overflow-auto pr-2 p-1 -m-1">
            {/* Sync Sources */}
            {syncSources.map((source) => {
              const job = getLatestJobForSource(source.id);
              const running = isSourceRunning(source.id);
              const isSelected = selectedJob === job?.id;

              return (
                <Card
                  key={source.id}
                  className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary' : 'hover:bg-muted/50'}`}
                  onClick={() => job && handleSelectJob(job.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2">
                        <div className={`p-1.5 rounded ${source.bgColor} ${source.color}`}>
                          <source.icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm">{source.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {source.description}
                          </div>
                          {job && (
                            <div className="mt-1 flex items-center gap-2">
                              {getStatusBadge(job.status)}
                              <span className="text-[10px] text-muted-foreground">
                                {job.itemsProcessed} items
                              </span>
                            </div>
                          )}
                          {/* Sub-job status summary */}
                          {job && subJobCounts[job.id] && (
                            <SubJobSummary subJobs={subJobCounts[job.id]} />
                          )}
                        </div>
                      </div>
                      {source.id === 'slack' ? (
                        /* Slack gets a dropdown with Recent and Full History options */
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              onClick={(e) => e.stopPropagation()}
                              disabled={running || triggerMutation.isPending}
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                            >
                              {running ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <Play className="h-3 w-3" />
                                  <ChevronDown className="h-2.5 w-2.5 ml-0.5" />
                                </>
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                            <DropdownMenuItem
                              onClick={() => triggerMutation.mutate({ source: source.id, mode: 'recent' })}
                            >
                              <Play className="h-3 w-3 mr-2" />
                              Recent (last 500 msgs)
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => triggerMutation.mutate({ source: source.id, mode: 'full' })}
                            >
                              <History className="h-3 w-3 mr-2" />
                              Full History (all msgs)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        /* Other sources get a simple button */
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerMutation.mutate({ source: source.id });
                          }}
                          disabled={running || triggerMutation.isPending}
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2"
                        >
                          {running ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Play className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                    </div>
                    {job?.error && (
                      <div className="mt-2 p-1.5 bg-destructive/10 rounded text-destructive text-[10px] truncate">
                        {job.error}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Recent Jobs List */}
            <Card>
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5" />
                  Recent Jobs
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                {isLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : jobs?.length ? (
                  <div className="space-y-1">
                    {jobs.slice(0, 10).map((job) => (
                      <div
                        key={job.id}
                        onClick={() => handleSelectJob(job.id)}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                          selectedJob === job.id ? 'bg-primary/10' : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className="capitalize text-[10px] h-5">
                            {job.source}
                          </Badge>
                          {getStatusBadge(job.status)}
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {job.startedAt && formatDistanceToNow(new Date(job.startedAt), { addSuffix: true })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] text-muted-foreground text-center py-2">
                    No sync jobs yet
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Log Viewer */}
          <Card className="flex flex-col h-full overflow-hidden">
            <CardHeader className="p-3 pb-2 border-b shrink-0">
              <div className="flex items-center justify-between h-6">
                <div className="flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-sm">Job Logs</CardTitle>
                  {selectedJobData && (
                    <>
                      <Badge variant="outline" className="capitalize text-[10px] h-5">
                        {selectedJobData.source}
                      </Badge>
                      {getStatusBadge(selectedJobData.status)}
                    </>
                  )}
                </div>
                <div className="flex gap-1 h-6">
                  {selectedJobData && (selectedJobData.status === 'running' || selectedJobData.status === 'pending') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-destructive hover:text-destructive text-[10px]"
                      onClick={() => cancelMutation.mutate(selectedJobData.id)}
                      disabled={cancelMutation.isPending}
                    >
                      <StopCircle className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                  )}
                  {selectedJobData && (selectedJobData.status === 'failed' || selectedJobData.status === 'cancelled') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[10px]"
                      onClick={() => retryMutation.mutate(selectedJobData.id)}
                      disabled={retryMutation.isPending}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Retry
                    </Button>
                  )}
                </div>
              </div>
              {/* Sub-job status balls - individual balls above job ID */}
              {selectedJob && subJobCounts[selectedJob]?.list && subJobCounts[selectedJob].list!.length > 0 && (
                <div className="mt-2">
                  <SubJobBalls subJobs={subJobCounts[selectedJob]} />
                </div>
              )}
              {selectedJobData && (
                <CardDescription className="text-[10px] font-mono mt-1">
                  {selectedJobData.id}
                </CardDescription>
              )}
            </CardHeader>

            {/* Log Content */}
            <div className="flex-1 bg-zinc-950 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-3 font-mono text-[11px] space-y-0.5">
                  {selectedJobData?.error && (
                    <div className="p-2 mb-2 bg-red-950/50 border border-red-900 rounded text-red-400">
                      Error: {selectedJobData.error}
                    </div>
                  )}
                  {jobLogs.length > 0 ? (
                    jobLogs.map((log, i) => (
                      <div key={i} className="flex gap-2 leading-relaxed">
                        <span className="text-zinc-600 select-none shrink-0">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        <span className={
                          log.level === 'error' ? 'text-red-400' :
                          log.level === 'warn' ? 'text-yellow-400' :
                          'text-zinc-300'
                        }>
                          {log.message}
                        </span>
                      </div>
                    ))
                  ) : selectedJobData ? (
                    <div className="text-zinc-500 text-center py-8">
                      {selectedJobData.status === 'running' || selectedJobData.status === 'pending' ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Waiting for logs...</span>
                        </div>
                      ) : (
                        <p>No logs available for this job</p>
                      )}
                    </div>
                  ) : (
                    <div className="text-zinc-500 text-center py-8">
                      Select a job to view logs
                    </div>
                  )}
                  <div ref={logsEndRef} />
                </div>
              </ScrollArea>
            </div>

            {/* Job Details Footer */}
            {selectedJobData && (
              <div className="p-2 border-t bg-muted/30 shrink-0">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <div className="flex gap-4">
                    <span>
                      <span className="text-foreground font-medium">{selectedJobData.itemsProcessed}</span> items
                    </span>
                    <span>
                      Started: {selectedJobData.startedAt ? new Date(selectedJobData.startedAt).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  {selectedJobData.completedAt && (
                    <span>
                      Completed: {new Date(selectedJobData.completedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
